import { Component, type EventEmitter, Event, Prop, h, Listen, State, Element } from '@stencil/core';
import { UiaasSearchLabelList } from '../../../../result-cards/subcomponents/uiaas-search-label-list';
import { GenericCardTitle } from '../../../../new-result-cards/generic-subcomponents/generic-card-title/generic-card-title';
import type { SearchDocument } from '../../../../../../api/model/response/documents/public/Document';
import { GenericCardEventsLayer } from '../../../../new-result-cards/generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import type { SearchProduct } from '../../../../../../api/model/response/documents/public/Product';
import { ResultCardType } from '../../../../new-result-cards/public/product-card/helpers/helpers';
import type { SearchResultClickEvent } from '../../../SearchResultClickEvent';
import { Strategies } from '../../../../../strategies/Strategies';
import type { labelProviderInterface } from '../../../../../LabelProviderInterface';

@Component({
  tag: 'dashboard-document-item',
  shadow: false,
  styleUrl: 'dashboard-documents-tile.scss',
})
export class DashboardDocumentItem {
  @Prop() readonly doc: SearchDocument;
  @Prop() readonly index: number;
  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly strategies: Strategies;
  @Prop() readonly section: string;
  @Prop() readonly showPlaceholder?: boolean;
  @Prop() readonly rtl?: boolean;
  @Prop() readonly lazyLoadImages: boolean = false;
  @Element() el: HTMLElement;

  @State() accessibilityMode;

  @Event({
    eventName: 'search:ResultCardClick',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

  @Listen('uiaas:accessibility', { target: 'window' })
  handleAccessibility(ev) {
      this.accessibilityMode = ev.detail;
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (fallback) => {
            return fallback;
        };
      }

      const gridItem = this.el.closest('.grid-item');
      if (gridItem && gridItem.clientWidth > 600) {
        gridItem.classList.add('double');
      }
  }

  connectedCallback() {
      const htmlElement = document.querySelector('html');
      const isAccessibilityOn = htmlElement.classList.contains('se2-accessibility');
      this.accessibilityMode = isAccessibilityOn;
  }

  render() {
      const sectionsWithoutDescription = ['software', 'documents', 'solutions', 'company'];
      const isDescriptionVisible = !sectionsWithoutDescription.includes(this.section) && this.doc?.description;
      const tabsWithoutPlaceholder = [
        'services',
        'support'
      ];

      const showImage = !tabsWithoutPlaceholder.includes(this.section) || this.showPlaceholder;
      const accessibilityModeClass = this.accessibilityMode ? '--accessibility' : '';

      return (
        <GenericCardEventsLayer
          clickEmitter={this.resultCardClickEventEmitter}
          doc={this.doc as unknown as SearchProduct}
          resutCardType={ResultCardType.Basic}
          index={this.index}
          section={this.section}
        >
          <div class="document__container result-card__container" dir={this.rtl ? 'rtl' : 'auto'}>
            {/* <a href={this.doc.url} data-card-area={'background'}>
              {showImage ? <section class="result-card__thumbnail-section">
                <uiaas-thumbnail src={this.doc.thumbnailUrl} custom-lazy-loading={this.lazyLoadImages} />
              </section> : ''}
            </a> */}
            <div class="document__content">
              <a href={this.doc.url} data-card-area={'background'}>
                <GenericCardTitle
                  url={this.doc.url}
                  text={this.doc.title}
                  strategies={this.strategies}
                />
                <div class="document__label-panel" tabindex="0">
                  <UiaasSearchLabelList
                    list={[
                      {
                        value: this.doc?.date,
                      },
                      {
                        value: this.doc?.category?.CT_CAT,
                        customValueClass: 'document__category' + accessibilityModeClass,
                      },
                      {
                        label: this.labelProvider(
                          'tab.label.latest-version',
                          'Latest version'
                        ),
                        value: this.doc?.version,
                      },
                    ]}
                  />
                </div>
              </a>
              {isDescriptionVisible ? <div class="document__description" innerHTML={this.doc?.description}></div> : ''}
              {this.doc?.attachments?.length > 0 && (
                <div class="document__download-container">
                  <qds-standalone-link
                    href={this.doc?.attachments[0]?.url}
                    data-card-area={'attachment'}
                    download={this.doc?.attachments[0]?.fileName}
                    icon-name={'arrow-down-tray'} icon-library={'default'}
                  >
                    <div class="document__download">
                      <span class="document__download-space document__download-title">
                        {this.doc?.attachments[0]?.fileName}
                      </span>
                      <span>{`(${this.doc?.attachments[0]?.length})`}</span>
                    </div>
                  </qds-standalone-link>
                </div>
              )}
            </div>
          </div>
        </GenericCardEventsLayer>
      );
  }
}
